import React from 'react';
import { compose, range, map, contains, head, last } from 'ramda';
import moment from 'moment';

import PropertyManager from '@synergycms_core/components/HoC/propertyManager';
import ScrollFocusComponent from '@synergycms_core/components/HoC/scrollFocusComponent';
import { withThemeJSS } from '@synergycms_core/components/providers/themeProvider';
import styleFile from './datepicker.js';
import Button from '../Button';
import RichText from '../RichText';
import classnames from 'classnames/bind';
import { withContent } from '@synergycms_core/components/providers/translateProvider';

export const DATEPICKER_TYPES = {
  SINGLE: 'single', // Selección individual de un solo día
  RANGE: 'fixedRange', // Selección de un rango de días fijo
  MULTI: 'multi' // Selección de múltiples días
}

class datepickerDay extends React.Component {
  
  static displayName = 'DatepickerDay';

  constructor(props) {
    super(props);
    this.cx = classnames.bind(props.theme.styles.classes);
  }

  render() {
    const { config, theme, content } = this.props;
    
    const { 
      day,
      stringDate,
      isSelected,
      startRange,
      endRange,
      disabled,
      datesSelected,
      isQuotaService,
    } = config;
        
    const dayTemplate = config.dayTemplate && config.dayTemplate({ 
      day,
      stringDate,
      isSelected, 
      startRange, 
      endRange, 
      disabled, 
      datesSelected
    });


    const formattedDate = day && day.format('dddd D MMMM, YYYY');

    const ariaLabel = formattedDate 
      ? `${formattedDate}${startRange ? ` ${content.t('_%Entrada%_')}` : ''}${endRange ? ` ${content.t('_%Salida%_')}` : ''}`
      : '';

    return (
      <Button
        onClick={config.selectRange(day)}
        innerStyle={[
          theme.styles.classes.day,
          isQuotaService && theme.styles.classes.quotaServiceDay,
          !!day && config.currentDate === config.stringDate
            ? theme.styles.classes.today
            : theme.styles.classes.notToday,
          !!day &&
          this.cx({
            isSelected: config.isSelected,
            startRange: isQuotaService ? false : config.startRange,
            endRange: isQuotaService ? false : config.endRange,
            moreSpecEndRage: isQuotaService ? false : config.endRange,
            disabled: config.disabled
          })
        ]}
        disabled={config.disabled}
        text={!dayTemplate && !!day && day.format('D')}
        ariaLabel={ariaLabel}
      >{!!dayTemplate && dayTemplate}</Button>
    );
  }
}

const DatepickerDay = compose(
  PropertyManager([
    'day',
    'selectRange',
    'isSelected',
    'startRange',
    'endRange',
    'stringDate',
    'currentDate',
    'disabled',
    'dayTemplate',
    'datesSelected',
    'isQuotaService'
  ]),
  withThemeJSS({ styleFile: styleFile.day, link: true }),
  withContent('i18n')
)(datepickerDay);

class datepickerMonth extends React.Component {
  static displayName = 'DatepickerMonth';

  render() {
    const { config, theme } = this.props;
    const { month, selectRange, datesSelected, DaySelectorTemplate, isQuotaService} = config;
    const startOfMonth = moment(month)
      .startOf('month')
      .startOf('week');
    const endOfMonth = moment(month).endOf('month');
    const monthNumber = month.month();
    const currentDate = moment().format('YYYY-MM-DD');

    const DayComponent = DaySelectorTemplate ? DaySelectorTemplate : DatepickerDay;

    return (
      <div className={[theme.computedInnerStyle, theme.styles.classes.monthWrapper ].join(' ')}>
        <RichText innerStyle={[theme.styles.classes.monthTitle]} text={month.format('MMMM YYYY')} ucFirst={true} />
        <div className={theme.styles.classes.dayList}>
          {moment.weekdaysShort(true).map(weekDay => (
            <div key={weekDay} className={theme.styles.classes.weekDays}>
              {weekDay.replace('.', '')}
            </div>
          ))}
        </div>
        <div className={theme.styles.classes.month}>
          {map(days => {
            const currentDay = startOfMonth.clone().add(days, 'day');
            const stringDate = currentDay.format('YYYY-MM-DD');
            const isSelected = contains(stringDate, datesSelected);
            const startRange = isSelected && stringDate === head(datesSelected);
            const endRange = isSelected && !startRange && stringDate === last(datesSelected);
            const disabled = stringDate < currentDate;

            return (
              <DayComponent
                key={stringDate}
                stringDate={stringDate}
                selectRange={selectRange}
                isSelected={isSelected}
                startRange={startRange}
                endRange={endRange}
                currentDate={currentDate}
                datesSelected={datesSelected}
                disabled={config.disableDay ? config.disableDay({ day: currentDay, stringDate, isSelected, startRange, endRange, disabled, datesSelected }) : disabled}
                day={monthNumber === currentDay.month() && currentDay}
                onOptionSelect={config.onOptionSelect}
                daySelectorOptions={config.daySelectorOptions}
                dayTemplate={config.dayTemplate}
                isQuotaService={isQuotaService}/>
            );
          }, range(0, endOfMonth.diff(startOfMonth, 'days') + 1))}
        </div>
      </div>
    );
  }
}

const DatepickerMonth = compose(
  PropertyManager([
    'month', 
    'selectRange', 
    'datesSelected', 
    'currentDate', 
    'dayTemplate', 
    'disableDay', 
    'DaySelectorTemplate', 
    'onOptionSelect', 
    'daySelectorOptions',
    'isQuotaService'
  ]),
  withThemeJSS({ styleFile: styleFile.monthly, link: true })
)(datepickerMonth);


class Datepicker extends React.Component {
  static displayName = 'Datepicker';
  constructor(props) {
    super(props);
    const { config } = props;

    this.state = {
      startSelected: (config.start && moment(config.start)) || null,
      endSelected: (config.end && moment(config.end)) || null,
      dates: [],
      currentFirstMonth: config.firstMonth || (config.start ? moment(config.start) : moment()),
      months: config.months || (this.props.theme.deviceType === 'MOBIL' ? 4 : 2)
    };

    this.monthsLoading = false;
    if(!this.props.config.elementToScroll){
      this.elementRef = React.createRef();
    }
    this.cx = classnames.bind(props.theme.styles.classes);
  }

  onScroll = e => {
    const calendarDomNode = this.elementRef ? this.elementRef.current : this.props.config.elementToScroll;

    const maxScrollValue = (calendarDomNode.scrollHeight - calendarDomNode.clientHeight) - 50 ||  document.documentElement.scrollHeight - 50;
    const scrollTop = calendarDomNode.scrollTop || (window.scrollY + window.innerHeight)
    const scrollVertical = calendarDomNode.scrollTop || window.scrollY
    this.props.config.onScroll && this.props.config.onScroll();
    if (scrollTop >= maxScrollValue && !this.monthsLoading) {
      this.monthsLoading = true;
      this.setState(() => ({
        months: this.state.months + 4
      }), () => {
        this.monthsLoading = false
        this.props.config.onChangeCalendar && this.props.config.onChangeCalendar({ currentFirstMonth: this.state.currentFirstMonth, moveTo: "next", months: this.state.months });
      });
    } else if (scrollVertical < 10 && !this.monthsLoading) {
      this.monthsLoading = true;
      const subrtractMonth = this.state.currentFirstMonth.clone().subtract(1, 'month');
      const minDate = (this.props.config.minDate && moment(this.props.config.minDate)) || moment();
      this.setState(() => {
        if (minDate && minDate < subrtractMonth.endOf("month")) {
          this.props.config.onChangeCalendar && this.props.config.onChangeCalendar({ currentFirstMonth: subrtractMonth, moveTo: "prev", months: this.state.months });
          return { currentFirstMonth: subrtractMonth };
        }
      }, () => (this.monthsLoading = false));
    }
  }

  componentDidMount = () => {
    if (this.props.theme.deviceType === 'MOBIL') {
      if(this.elementRef){
        this.elementRef.current.scroll(0, 1);
      }else{
        window.addEventListener('scroll', this.onScroll);
      }
    }
  }

  componentDidUpdate = () => {
    const { customDatesModel } = this.props.config;
    let currentSelectedDates = this.buildSelectedDates();

    if(customDatesModel && customDatesModel.length !== currentSelectedDates.length){
      currentSelectedDates = customDatesModel;
      this.setState({ dates: customDatesModel.map(date => moment(date))});
    }
  }

  componentWillUnmount = () => {
    window.removeEventListener('scroll', this.onScroll);
  }

  selectRange = currentSelected => e => {
    e && e.preventDefault();
    this.setState(
      (state) => {
        const typeSelection = this.props.config.typeSelection || {};
        if (typeSelection.type === DATEPICKER_TYPES.RANGE) {
            return {
              startSelected: moment(currentSelected),
              endSelected: moment(currentSelected).add(typeSelection.count, "days")
            }
        } else if (typeSelection.type === DATEPICKER_TYPES.SINGLE) {
            if(this.state.date && this.state.date.format("YYYY-MM-DD") === currentSelected.format("YYYY-MM-DD")){
              return { date: null };
            }
            return { date: moment(currentSelected) };
        } else if (typeSelection.type === DATEPICKER_TYPES.MULTI) {
          const { dates } = state;
          if(dates.find(date => date.format("YYYY-MM-DD") === currentSelected.format("YYYY-MM-DD"))){
            return {
              dates: dates.filter((date) => {
                return date.format("YYYY-MM-DD") !== currentSelected.format("YYYY-MM-DD")
              })
            }
          }
          return { dates: dates.concat([currentSelected])}
        } else {
          const { startSelected, endSelected } = state;
          if (currentSelected && (!startSelected || startSelected.diff(currentSelected) || endSelected)) {
            if (startSelected && endSelected) {
              return { startSelected: currentSelected, endSelected: null };
            } else if (startSelected && !endSelected) {
              let start, end;
              if (startSelected < currentSelected) {
                start = startSelected;
                end = currentSelected;
              } else {
                start = currentSelected;
                end = startSelected;
              }
              return {
                startSelected: start,
                endSelected: end
              };

            } else {
              return { startSelected: currentSelected };
            }
          }
        }
      },() => {
        const { 
          currentFirstMonth, 
          months, 
          startSelected, 
          endSelected, 
          date, 
          dates,
        } = this.state;

        if (this.props.theme.deviceType === 'DESK' && startSelected && !endSelected) {
          if (
            currentFirstMonth
              .clone()
              .add(months - 1, 'month')
              .endOf('month')
              .format('YYYY-MM-DD') === startSelected.format('YYYY-MM-DD')
          ) {
            this.changeMonth('next')();
          }
        }

        this.props.config.onRangeChange?.({
          ...(!!startSelected && {start: startSelected && startSelected.format('YYYY-MM-DD')}),
          ...(!!endSelected && {end: endSelected && endSelected.format('YYYY-MM-DD')}),
          ...(!!date && {date: date && date.format('YYYY-MM-DD')}),
          ...(!!dates && {dates: dates && dates.map(date => date.format('YYYY-MM-DD'))})
        });

      }
    );
  };

  changeMonth = moveTo => e => {
    e && e.preventDefault();
    this.setState(state => {
      const currentCalendar = { currentFirstMonth: state.currentFirstMonth.clone().add((moveTo === 'next' ? 1 : -1), 'month') };
      this.props.config.onChangeCalendar && this.props.config.onChangeCalendar({ ...currentCalendar, moveTo: moveTo, months: state.months });
      return currentCalendar;
    });
  };

  buildSelectedDates = () => {
    let selectedDates = [];
    const { startSelected, endSelected, date, dates } = this.state;
    if (startSelected && endSelected) {
      if (startSelected <= endSelected) {
        selectedDates = map(days => {
          return startSelected
            .clone()
            .add(days, 'day')
            .format('YYYY-MM-DD');
        }, range(0, endSelected.diff(startSelected, 'days') + 1)).sort();
      } else {
        selectedDates = map(days => {
          return endSelected
            .clone()
            .add(days, 'day')
            .format('YYYY-MM-DD');
        }, range(0, startSelected.diff(endSelected, 'days') + 1)).sort();
      }
    } else if (startSelected || endSelected) {
      selectedDates = startSelected
        ? [startSelected.clone().format('YYYY-MM-DD')]
        : [endSelected.clone().format('YYYY-MM-DD')];
    } else if (date) {
      selectedDates = [date.clone().format('YYYY-MM-DD')];
    } else if (dates.length) {
      selectedDates = map(date => date.format('YYYY-MM-DD'), dates).sort();
    } else {
      selectedDates = [];
    }
    return selectedDates;
  }

  isModalBreak = ()=>{
    return window.innerWidth < this.props.theme.themeValues.modalBreak;
  }

  render() {
    const { theme, config } = this.props;
    const { classes } = theme.styles;
    const { startSelected, endSelected, currentFirstMonth, date, dates } = this.state;
    const monthsList = map(range => currentFirstMonth.clone().add(range, 'month'), range(0, this.state.months));
    
    const datesSelected = this.buildSelectedDates();
    const { customDatesModel, mobileArrows = false } = config;

    const hasScrollEvent = this.isModalBreak() && this.elementRef && !mobileArrows;

    const isCurrentMonth = currentFirstMonth.month() === moment().month() && currentFirstMonth.year() === moment().year();

    const datePickerClassNames = [
      theme.computedInnerStyle,
      theme.styles.classes[theme.theme + '_datepickerEngineContainer'],
      config.mobileArrows ? classes.mobileArrows : ''
    ].join(' ');

    const showArrowButtons = !this.isModalBreak() || mobileArrows;

    return (
      <div className={datePickerClassNames} ref={this.elementRef} onScroll={hasScrollEvent ? this.onScroll : undefined}>
        <div className={classes.datepicker}>
          <div className={[classes.datepickerRelative, theme.styles.classes[theme.theme+'_datepickerEnginePortal']].join(' ')}>
            {(showArrowButtons) && (
              <div className={classes.buttonContainer}>
                <Button
                  onClick={this.changeMonth('prev')}
                  innerStyle={[classes.buttonCalendar, isCurrentMonth ? classes.disabledButton : classes.enabledButton]}
                  icon={{ content: '\\' }}
                  disabled={isCurrentMonth}
                  ariaLabel={'previous'}
                />
              </div>
            )}
            <div className={[classes.datepickerMonthContainer, this.cx({ monthContQuotaService: config.isQuotaService,})].join(' ')}>
              {monthsList.map(month => {
                return (
                  <DatepickerMonth
                    disableDay={config.disableDay}
                    dayTemplate={config.dayTemplate}
                    key={month.format('YYYY-MM')}
                    selectRange={this.selectRange}
                    datesSelected={customDatesModel ? customDatesModel : datesSelected}
                    DaySelectorTemplate={config.DaySelectorTemplate}
                    isQuotaService={config.isQuotaService}
                    onOptionSelect={config.onOptionSelect}
                    daySelectorOptions={config.daySelectorOptions}
                    month={month}/>
                );
              })}
            </div>
            {(showArrowButtons) &&  (
              <div className={[classes.buttonContainer, classes.buttonRight].join(' ')}>
                <Button
                  onClick={this.changeMonth('next')}
                  innerStyle={classes.buttonCalendar}
                  icon={{ unicodeContent: 'e000' }}
                  ariaLabel={'next'}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default compose(
  PropertyManager([
    'onScroll',
    'months',
    'firstMonth',
    'start',
    'end',
    'onRangeChange',
    'minDate',
    'dayTemplate',
    "onChangeCalendar",
    'typeSelection',
    'disableDay',
    'elementToScroll',
    'DaySelectorTemplate',
    'onOptionSelect',
    'daySelectorOptions',
    'customDatesModel',
    'mobileArrows',
    'isQuotaService',
  ]),
  ScrollFocusComponent({scrollWindow:false}),
  withThemeJSS({ styleFile, link: true })
)(Datepicker); 
 